import { useMemo } from 'react';
import Pie, { defaultColorPalette } from 'core/chart/pie';
import { colors } from 'ui/theme';
import { splitArray, sumObjectListByKey } from 'utils/arrayHelpers';

import { useSummaryQueryContext } from '../contexts/query';
import { useEventSelectionContext } from '../contexts/selection';
import { ExtendedEventInfo } from '../types';
import { ChartLoader } from './ChartLoader';
import { TotalEventCountBox } from './TotalEventCountBox';
import { ChartSection, PieChartContainer, ChartFootnote } from './styled';

export const OTHER_LABEL = 'Other events*';
export const OTHER_FOOTNOTE =
  '*Includes all events that account for less than 5% of the total event count';

const options = {
  chart: {
    style: {
      fontFamily: 'Sofia Pro',
    },
  },
  exporting: { enabled: false },
  tooltip: { enabled: false },
  plotOptions: {
    pie: {
      borderColor: '#FFFFFF',
      borderWidth: '1px',
      size: '70%',
      innerSize: '50%',
      dataLabels: {
        enabled: true,
        connectorShape: 'straight',
        x: -3,
        distance: 20,
        format: `<span style="color:${colors.textSecondary};font-size:14px;">{point.name}  </span>\
            <br/><span style="font-size:20px;">{point.percentage:.2f}%</span>`,
        style: {
          fontWeight: 400,
        },
      },
      colors: defaultColorPalette,
    },
  },
};

type ChartData = {
  name: string;
  y: number;
};

export const sortByY = (chartData: ChartData[]) => [...chartData]?.sort((a, b) => b.y - a.y) || [];

/**
 * Converts raw event data into data usable in a HighCharts pie chart.
 * Determines which event types should be directly displayed and which
 * should be looped into an "Other events" section.
 *
 * @param { FilteredEvents[] } eventData [{eventName, count, eventType, eventPercent},...]
 * @param { Number } minPercent smallest percentage that should be displayed
 * @returns [{name,y}, ...] HighCharts-compatible data
 */
export const getDonutChartData = (eventData: ExtendedEventInfo[], minPercent: number) => {
  if (eventData.length === 0) {
    return [];
  }

  const allChartData = sortByY(
    eventData?.map((event) => ({
      name: event.eventLabel ?? event.eventName,
      y: event.eventPercent,
    })),
  );

  const [chartData, other] = splitArray(allChartData, (datum) => datum.y > minPercent);
  const otherData = [];
  if (other.length > 0) {
    const otherPercent = sumObjectListByKey(other, 'y');
    otherData.push({ name: OTHER_LABEL, y: otherPercent });
  }
  return [chartData, otherData];
};

export const EventSummaryChart = () => {
  const { selected } = useEventSelectionContext();
  const { isError, isLoading } = useSummaryQueryContext();
  const [chartData = [], otherData = []] = useMemo(
    () => getDonutChartData(selected, 0.05),
    [selected],
  );
  const noData = [...chartData, ...otherData].length === 0;

  if (isLoading) {
    return <ChartLoader />;
  }

  if (isError || noData) {
    return null;
  }

  const showOtherFootnote = otherData.length > 0;

  return (
    <ChartSection data-test="event-summary-chart">
      <TotalEventCountBox />
      <PieChartContainer>
        <Pie
          // @ts-ignore
          data={[{ data: [...chartData, ...otherData] }]}
          options={options}
        />
        {showOtherFootnote && <ChartFootnote>{OTHER_FOOTNOTE}</ChartFootnote>}
      </PieChartContainer>
    </ChartSection>
  );
};
